import { forwardRef } from 'react';
import { REGIONS, Category, Region } from '../types';
import { type Lang, DEFAULT_LANG, getUi, regionName, categoryName } from '../../functions/lib/i18n';

interface FilterBarProps {
  selectedCategory: string;
  selectedRegion: string;
  categories: Category[];
  showShorts: boolean;
  onCategoryChange: (category: string) => void;
  onRegionChange: (region: string) => void;
  onShowShortsChange: (show: boolean) => void;
  onRandom?: () => void;
  onShare?: () => void;
  shareCopied?: boolean;
  disabled?: boolean;
  isStuck?: boolean;
  lang?: Lang;
}

/**
 * Filter bar component with category and region dropdowns
 * LTS: Simplified - removed time period buttons
 */
export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(function FilterBar({
  selectedCategory,
  selectedRegion,
  categories,
  showShorts,
  onCategoryChange,
  onRegionChange,
  onShowShortsChange,
  onRandom,
  onShare,
  shareCopied = false,
  disabled = false,
  isStuck = false,
  lang = DEFAULT_LANG,
}, ref) {
  const ui = getUi(lang);
  return (
    <div ref={ref} className={`filter-bar ${isStuck ? 'stuck' : ''}`}>
      <div className="filter-row">
        <div className="filter-group">
          <label htmlFor="category-select" className="filter-label">{ui.categoryLabel}</label>
          <select
            id="category-select"
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            disabled={disabled}
          >
            {categories.map((category: Category) => (
              <option key={category.id} value={category.id}>
                {categoryName(lang, category.id, category.name)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="region-select" className="filter-label">{ui.regionLabel}</label>
          <select
            id="region-select"
            className="filter-select region-select"
            value={selectedRegion}
            onChange={(e) => onRegionChange(e.target.value)}
            disabled={disabled}
          >
            {REGIONS.map((region: Region) => (
              <option key={region.id} value={region.id}>
                {region.flag} {regionName(lang, region.id, region.name)}
              </option>
            ))}
          </select>
        </div>

        <button
          className={`shorts-toggle ${showShorts ? 'active' : ''}`}
          onClick={() => onShowShortsChange(!showShorts)}
          disabled={disabled}
          role="switch"
          aria-checked={showShorts}
          title={showShorts ? ui.shortsOnTitle : ui.shortsOffTitle}
        >
          <span className="shorts-toggle-dot" aria-hidden="true" />
          {showShorts ? ui.shortsOn : ui.shortsOff}
        </button>

        {(onRandom || onShare) && (
          <div className="filter-actions">
            {onRandom && (
              <button
                type="button"
                className="action-button"
                onClick={onRandom}
                disabled={disabled}
                title={ui.randomTitle}
              >
                🎲 <span className="action-label">{ui.randomLabel}</span>
              </button>
            )}
            {onShare && (
              <button
                type="button"
                className="action-button"
                onClick={onShare}
                title={ui.shareTitle}
              >
                {shareCopied ? `✓ ${ui.shareCopiedLabel}` : <>🔗 <span className="action-label">{ui.shareLabel}</span></>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
